// src/pages/SanctionList.jsx
import React, { useState, useEffect, useMemo } from 'react';
import {
  GridComponent, ColumnsDirective, ColumnDirective,
  Resize, Sort, ContextMenu, Filter, Page, ExcelExport, PdfExport, Inject
} from '@syncfusion/ej2-react-grids';
import { Header } from '../Components';
import axios from 'axios';
import {
  FaSearch, FaFileExcel, FaFilePdf, FaTimes
} from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';
import { useBaseUrl } from '../Contexts/BaseUrlContext';

const sanctionGrid = [
  {
    field: 'client_id',
    headerText: 'Client Id.',
    width: 120,
    textAlign: 'Center',
    headerTemplate: () => <span className="font-bold">Client Id.</span>,
    template: (props) => (
      <div
        className="text-center cursor-pointer text-blue-500 hover:underline"
        onClick={() => props.viewHandler(props.client_id)}
      >
        {props.client_id}
      </div>
    )
  },
  {
    field: 'client_name',
    headerText: 'Client',
    width: 200,
    textAlign: 'Center',
    headerTemplate: () => <span className="font-bold">Client</span>,
  },
  {
    field: 'phone_num',
    headerText: 'Contact',
    width: 140,
    textAlign: 'Center',
    headerTemplate: () => <span className="font-bold">Contact</span>,
    template: (props) => <div className="truncate">{props.phone_num}{props.phone_num2 ? `, ${props.phone_num2}` : ''}</div>
  },
  {
    field: 'address',
    headerText: 'Address',
    width: 180,
    textAlign: 'Center',
    headerTemplate: () => <span className="font-bold">Address</span>,
  },
  {
    field: 'loan_type',
    headerText: 'Loan Type',
    width: 160,
    textAlign: 'Center',
    headerTemplate: () => <span className="font-bold">Loan Type</span>,
  },
  {
    field: 'sanction_loan',
    headerText: 'Sanction Loan',
    width: 140,
    textAlign: 'Center',
    headerTemplate: () => <span className="font-bold">Sanction Loan</span>,
  },
  {
    field: 'disbursed_date',
    headerText: 'Disbursed Date',
    width: 140,
    textAlign: 'Center',
    headerTemplate: () => <span className="font-bold">Disbursed Date</span>,
  },
  {
    field: 'loan_status',
    headerText: 'Status',
    width: 130,
    textAlign: 'Center',
    headerTemplate: () => <span className="font-bold">Status</span>,
  },
  {
    field: 'bank_finance',
    headerText: 'Bank/Finance',
    width: 160,
    textAlign: 'Center',
    headerTemplate: () => <span className="font-bold">Bank/Finance</span>,
  },
  {
    field: 'reference_by',
    headerText: 'Reference By',
    width: 200,
    textAlign: 'Center',
    headerTemplate: () => <span className="font-bold">Reference By</span>,
  },
   {
    field: 'Actions',
    headerText: 'Actions',
    width: 160,
    textAlign: 'Center',
    headerTemplate: () => <span className="font-bold">Actions</span>,
    template: (props) => (
      <div className="flex flex-col items-center gap-2">
        <button
          className="px-3 py-1 bg-green-600 text-white rounded hover:bg-green-700 text-sm w-24 disabled:opacity-60"
          onClick={() => props.openDisburseModal(props.client_id)}
          title="Sanction"
          disabled={props.disburseProcessing === props.client_id}
        >
          {props.disburseProcessing === props.client_id ? 'Processing...' : 'Disbursed'}
        </button>
      </div>
    ),
  },
];

const SanctionList = () => {
  const [data, setData] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');

  // NEW: Filters
  const [startDate, setStartDate] = useState(''); // yyyy-mm-dd
  const [endDate, setEndDate] = useState('');
  const [monthFilter, setMonthFilter] = useState(''); // 1-12
  const [yearFilter, setYearFilter] = useState(''); // yyyy

  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [selectedRow, setSelectedRow] = useState(null);

  // Disburse modal state
  const [showDisburseModal, setShowDisburseModal] = useState(false);
  const [disburseClientId, setDisburseClientId] = useState(null);
  const [disburseDate, setDisburseDate] = useState('');
  const [disburseRemarks, setDisburseRemarks] = useState('');
  const [disburseProcessing, setDisburseProcessing] = useState(null);

  const navigate = useNavigate();
  const { baseUrl } = useBaseUrl();

  // developer-provided local image path (kept for reference/use if needed)
  const devImagePath = '/mnt/data/c5afb376-78f6-4dcb-bbdd-ab1bb0d56157.png';

  useEffect(() => {
    const fetchList = async () => {
      setIsLoading(true);
      setErrorMessage('');
      const token = localStorage.getItem('authToken');
      if (!token) {
        setErrorMessage('Please log in to access this page.');
        navigate('/');
        setIsLoading(false);
        return;
      }

      try {
        const formData = new FormData();
        formData.append('action', 'loan_list');
        formData.append('type', 'Sanction File');

        const resp = await axios.post(
          `${baseUrl}bank-system/loan.php`,
          formData,
          {
            headers: {
              'Content-Type': 'multipart/form-data',
              Authorization: `Bearer ${token}`,
            },
            withCredentials: true,
          }
        );

        console.log('Sanction File List Response:', resp.data);

        if (resp.data && resp.data.status === true && Array.isArray(resp.data.data)) {
          setData(resp.data.data);
        } else {
          setData([]);
          setErrorMessage(resp.data?.message || 'Failed to fetch sanction file list.');
        }
      } catch (err) {
        console.error('Axios Error fetching sanction list:', err.response || err);
        setErrorMessage(err.response?.data?.message || 'Error fetching sanction file list.');
        if (err.response?.status === 401) navigate('/');
      } finally {
        setIsLoading(false);
      }
    };

    fetchList();
  }, [navigate, baseUrl]);

  // View handler - navigates to user profile page
  const viewHandler = (client_id) => {
    if (!client_id) {
      setErrorMessage('Invalid client id.');
      return;
    }
    navigate(`/user/${client_id}`);
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text).then(() => {
      alert('Copied to clipboard!');
    }).catch((err) => {
      console.error('Copy failed:', err);
      alert('Copy failed');
    });
  };

  // Open modal to enter disbursed details
  const openDisburseModal = (client_id) => {
    setErrorMessage('');
    setDisburseClientId(client_id);
    setDisburseDate(new Date().toISOString().slice(0, 10));
    setDisburseRemarks('');
    setShowDisburseModal(true);
  };

  // Submit disburse
  const submitDisburse = async () => {
    if (!disburseClientId) {
      setErrorMessage('Client ID missing.');
      return;
    }

    const token = localStorage.getItem('authToken');
    if (!token) {
      setErrorMessage('Please log in to perform this action.');
      navigate('/');
      return;
    }

    try {
      setDisburseProcessing(disburseClientId);
      setErrorMessage('');

      const formData = new FormData();
      formData.append('action', 'loan_status');
      formData.append('client_id', disburseClientId);
      formData.append('loan_status', 'Disbursed File');
      formData.append('disbursed_date', disburseDate || '');
      formData.append('remarks', disburseRemarks || '');

      const updateUrl = `${baseUrl}bank-system/loan.php`;

      const resp = await axios.post(updateUrl, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
          Authorization: `Bearer ${token}`,
        },
        withCredentials: true,
      });

      console.log('Disburse API response:', resp.data);

      if (resp.data && resp.data.status === true) {
        setData(prev => prev.filter(it => it.client_id !== disburseClientId));
        if (selectedRow && selectedRow.client_id === disburseClientId) setSelectedRow(null);
        alert(resp.data.message || 'Loan status updated successfully.');
        setShowDisburseModal(false);
      } else {
        setErrorMessage(resp.data?.message || 'Failed to update loan status.');
      }
    } catch (err) {
      console.error('Axios Error updating loan status:', err.response || err);
      setErrorMessage(err.response?.data?.message || 'Error updating loan status.');
      if (err.response?.status === 401) navigate('/');
    } finally {
      setDisburseProcessing(null);
    }
  };

  // ------------------------
  // Date helper (try several fields)
  // ------------------------
  const getItemDate = (item) => {
    const candidates = ['disbursed_date', 'last_update', 'created_at', 'applied_on', 'applied_at'];
    for (const k of candidates) {
      const v = item[k];
      if (!v || v === '0000-00-00' || v === '0000-00-00 00:00:00') continue;
      const s = String(v).trim();
      if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return new Date(s + 'T00:00:00');
      const parsed = new Date(s);
      if (!isNaN(parsed.getTime())) return parsed;
    }
    return null;
  };

  // ------------------------
  // Filtering (search + date range + month + year)
  // ------------------------
  const filtered = useMemo(() => {
    const q = searchTerm.trim().toLowerCase();
    const start = startDate ? new Date(startDate + 'T00:00:00') : null;
    const end = endDate ? new Date(endDate + 'T23:59:59') : null;
    const month = monthFilter ? Number(monthFilter) : null;
    const year = yearFilter ? Number(yearFilter) : null;

    return data.filter((item) => {
      // Quick search across fields
      if (q) {
        const ok =
          (item.client_name || '').toLowerCase().includes(q) ||
          (item.client_id || '').toLowerCase().includes(q) ||
          (item.phone_num || '').toLowerCase().includes(q) ||
          (item.reference_by || '').toLowerCase().includes(q);
        if (!ok) return false;
      }

      const d = getItemDate(item);

      if (start && (!d || d < start)) return false;
      if (end && (!d || d > end)) return false;

      if (month) {
        if (!d) return false;
        if (d.getMonth() + 1 !== month) return false;
      }

      if (year) {
        if (!d) return false;
        if (d.getFullYear() !== year) return false;
      }

      return true;
    });
  }, [data, searchTerm, startDate, endDate, monthFilter, yearFilter]);

  const gridData = filtered.map((item) => ({
    ...item,
    viewHandler,
    openDisburseModal,
    disburseProcessing,
    copyToClipboard,
  }));

  const clearFilters = () => {
    setSearchTerm('');
    setStartDate('');
    setEndDate('');
    setMonthFilter('');
    setYearFilter('');
  };

  return (
    <div className="max-w-7xl mx-auto p-4 sm:p-6 lg:p-8 animate-fadeIn min-h-screen">
      <Header category="Dashboard" title="Sanction File" />

      {errorMessage && (
        <div className="mb-4 p-4 bg-red-100 text-red-700 rounded-lg flex items-center">
          <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
          </svg>
          {errorMessage}
        </div>
      )}

      {/* Search + Export Row */}
      <div className="mb-6 flex flex-col sm:flex-row gap-4">
        <div className="relative flex-1">
          <FaSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            placeholder="Search by client name, phone, id or reference..."
            className="w-full pl-10 pr-4 py-2 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500 transition duration-300 bg-white shadow-sm"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>

        <div className="flex gap-2">
          <button
            className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition duration-300 shadow-md disabled:opacity-50"
            onClick={() => document.getElementById('sanctionGrid').ej2_instances[0].excelExport()}
            disabled={isLoading}
          >
            <FaFileExcel className="mr-2" /> Export Excel
          </button>
          <button
            className="flex items-center px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition duration-300 shadow-md disabled:opacity-50"
            onClick={() => document.getElementById('sanctionGrid').ej2_instances[0].pdfExport()}
            disabled={isLoading}
          >
            <FaFilePdf className="mr-2" /> Export PDF
          </button>
        </div>
      </div>

      {/* FILTER ROW (Separate clean row) */}
      <div className="mb-6 grid grid-cols-1 md:grid-cols-5 gap-4 p-4 rounded-lg">
        {/* Start Date */}
        <div>
          <label className="text-sm text-gray-600">Start Date</label>
          <input
            type="date"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
            className="w-full mt-1 p-2 border rounded"
          />
        </div>

        {/* End Date */}
        <div>
          <label className="text-sm text-gray-600">End Date</label>
          <input
            type="date"
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
            className="w-full mt-1 p-2 border rounded"
          />
        </div>

        {/* Month */}
        <div>
          <label className="text-sm text-gray-600">Month</label>
          <select
            value={monthFilter}
            onChange={(e) => setMonthFilter(e.target.value)}
            className="w-full mt-1 p-2 border rounded"
          >
            <option value="">All Months</option>
            {Array.from({ length: 12 }).map((_, i) => (
              <option key={i} value={i + 1}>
                {new Date(0, i).toLocaleString('default', { month: 'long' })}
              </option>
            ))}
          </select>
        </div>

        {/* Year */}
        <div>
          <label className="text-sm text-gray-600">Year</label>
          <select
            value={yearFilter}
            onChange={(e) => setYearFilter(e.target.value)}
            className="w-full mt-1 p-2 border rounded"
          >
            <option value="">All Years</option>
            {/* simple range; adjust if you want dynamic years from data */}
            {Array.from({ length: 6 }).map((_, i) => {
              const y = 2022 + i;
              return <option key={y} value={y}>{y}</option>;
            })}
          </select>
        </div>

        {/* Clear */}
        <div className="flex items-end">
          <button
            onClick={() => {
              setStartDate(''); setEndDate(''); setMonthFilter(''); setYearFilter('');
            }}
            className="w-full bg-gray-600 text-white py-2 rounded hover:bg-gray-700"
          >
            Clear Filters
          </button>
        </div>
      </div>

      {isLoading && (
        <div className="flex justify-center items-center py-4">
          <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-blue-600"></div>
        </div>
      )}

      <div className="bg-white rounded-xl shadow-lg overflow-hidden">
        <GridComponent
          id="sanctionGrid"
          dataSource={gridData}
          allowPaging
          allowSorting
          allowExcelExport
          allowPdfExport
          allowFiltering
          allowResizing
          pageSettings={{ pageSize: 10, pageSizes: [10, 20, 50] }}
          filterSettings={{ type: 'Menu', showFilterBarStatus: true }}
        >
          <ColumnsDirective>
            {sanctionGrid.map((c, i) => <ColumnDirective key={i} {...c} />)}
          </ColumnsDirective>
          <Inject services={[Resize, Sort, ContextMenu, Filter, Page, ExcelExport, PdfExport]} />
        </GridComponent>
      </div>

      {/* Details Modal */}
      {selectedRow && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 animate-fadeIn">
          <div className="bg-white rounded-lg p-6 w-full max-w-2xl max-h-[80vh] overflow-y-auto shadow-2xl flex flex-col">
            <div className="flex justify-between items-center mb-4 sticky top-0 bg-white z-10 border-b border-gray-200 pb-2">
              <h2 className="text-xl font-bold text-gray-900">Client Details</h2>
              <button
                className="text-gray-500 hover:text-gray-700 transition-colors"
                onClick={() => setSelectedRow(null)}
              >
                <FaTimes size={22} />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto space-y-4 text-gray-700">
              <div className="grid grid-cols-2 gap-4">
                <div><strong>Client Id:</strong></div>
                <div className="text-blue-600">{selectedRow.client_id}</div>

                <div><strong>Client UID:</strong></div>
                <div>{selectedRow.client_uid || '-'}</div>

                <div><strong>Client Name:</strong></div>
                <div>{selectedRow.client_name || '-'}</div>

                <div><strong>Contact 1:</strong></div>
                <div>{selectedRow.phone_num || '-'}</div>

                <div><strong>Contact 2:</strong></div>
                <div>{selectedRow.phone_num2 || '-'}</div>

                <div><strong>Address:</strong></div>
                <div>{selectedRow.address || '-'}</div>

                <div><strong>Loan Type:</strong></div>
                <div>{selectedRow.loan_type || '-'}</div>

                <div><strong>Loan Status:</strong></div>
                <div>{selectedRow.loan_status || '-'}</div>

                <div><strong>Required Loan:</strong></div>
                <div>{selectedRow.required_loan || '-'}</div>

                <div><strong>Sanction Loan:</strong></div>
                <div>{selectedRow.sanction_loan || '-'}</div>

                <div><strong>Bank / Finance:</strong></div>
                <div>{selectedRow.bank_finance || '-'}</div>

                <div><strong>Reference By:</strong></div>
                <div>{selectedRow.reference_by || '-'}</div>

                <div><strong>Disbursed Date:</strong></div>
                <div>{selectedRow.disbursed_date && selectedRow.disbursed_date !== '0000-00-00' ? selectedRow.disbursed_date : '-'}</div>

                <div><strong>Remark:</strong></div>
                <div className="col-span-1">{selectedRow.remark || '-'}</div>
              </div>

              <div className="mt-3">
                <button
                  className="px-3 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700 mr-2"
                  onClick={() => {
                    copyToClipboard(JSON.stringify(selectedRow, null, 2));
                    alert('Client JSON copied to clipboard');
                  }}
                >
                  Copy JSON
                </button>
              </div>
            </div>

            <div className="mt-6 flex justify-end gap-2 sticky bottom-0 bg-white border-t border-gray-200 pt-4">
              <button
                className="px-4 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700 transition-colors"
                onClick={() => setSelectedRow(null)}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Disburse Modal */}
      {showDisburseModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 animate-fadeIn">
          <div className="bg-white rounded-lg p-6 w-full max-w-lg shadow-2xl">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold">Disburse Loan</h3>
              <button
                type="button"
                className="text-gray-500 hover:text-gray-700"
                onClick={() => setShowDisburseModal(false)}
              >
                <FaTimes size={20} />
              </button>
            </div>

            <div className="space-y-3 text-gray-700">
              <div className="grid grid-cols-2 gap-3">
                <div className="text-sm font-medium">Client Id</div>
                <div className="text-sm text-blue-600">{disburseClientId}</div>

                <div className="text-sm font-medium">Loan Status</div>
                <div className="text-sm">Disbursed File</div>

                <div className="col-span-2">
                  <label className="block text-sm font-medium mb-1">Disbursed Date</label>
                  <input
                    type="date"
                    value={disburseDate}
                    onChange={(e) => setDisburseDate(e.target.value)}
                    className="w-full border rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    required
                  />
                </div>

                <div className="col-span-2">
                  <label className="block text-sm font-medium mb-1">Remarks</label>
                  <textarea
                    value={disburseRemarks}
                    onChange={(e) => setDisburseRemarks(e.target.value)}
                    className="w-full border rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    rows={4}
                    placeholder="e.g., App ID (L.A), Finance Name, Banker Name"
                  />
                </div>
              </div>
            </div>

            <div className="mt-5 flex justify-end gap-2">
              <button
                type="button"
                className="px-4 py-2 bg-gray-300 rounded hover:bg-gray-400"
                onClick={() => setShowDisburseModal(false)}
                disabled={!!disburseProcessing}
              >
                Cancel
              </button>
              <button
                type="button"
                className="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700 disabled:opacity-60"
                onClick={submitDisburse}
                disabled={!!disburseProcessing}
              >
                {disburseProcessing ? 'Submitting...' : 'Submit'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SanctionList;
